<!-- SurescriptsRecordLocatorInterfaceSetup.vue -->
<script lang="ts" setup>
import { computed, onBeforeMount, reactive, ref } from 'vue';
import { DisplayStyle } from '../../Enums/UiEnums';
import { AppScreens } from '../../Enums/ChoiceEnums';
import { ActionButtonStyle } from '../../lib/Types/Types';
import { ChoiceApi } from '../../LibMain';
import { popAlert, showToast } from '../../lib/Notification';
import { IRecordLocatorOrganizationAndSurescriptsInfo, IRecordLocatorOrganizationInfo } from '../../lib/Api/SurescriptsRecordLocator';
import AlertSection from '../../components/Messages/AlertSection.vue';
import TextInput from '../../components/TextInput.vue';
import PhoneInput from '../../components/Inputs/PhoneInput.vue';
import Row from '../../components/Section/Row.vue';
import GeographicStateSelect from '../../components/Select/GeographicStateSelect.vue';
import PostalCode from '../../components/Inputs/PostalCode.vue';
import ActionButtonV2 from '../../components/ActionButtonV2.vue';
import PageTitle from '../../components/PageTitle.vue';
import Validator from '../../components/Validator.vue';
import MessageBadge from '../../components/Messages/MessageBadge.vue';
import MessageText from '../../components/Messages/MessageText.vue';

    const compData = reactive<{
        initialized?: boolean,
        orgAndSurescriptsInfo?: IRecordLocatorOrganizationAndSurescriptsInfo,
        savingOrgInfo?: boolean,
    }>({});

    const validateSection = ref();    

    onBeforeMount(async () => 
    {
        compData.orgAndSurescriptsInfo = await ChoiceApi.surescriptsRLE.getRecordLocatorOrganizationInfo();
        if (!compData.orgAndSurescriptsInfo)
        {
            await popAlert("Failed to retrieve organization information for Surescripts Record Locator.", DisplayStyle.Danger);
            return;            
        }
        compData.initialized = true;
    });

    const orgInfo = computed<IRecordLocatorOrganizationInfo|undefined>(() => compData.orgAndSurescriptsInfo?.orgInfo);
    const homeCommunityId = computed<string|undefined>(() => compData.orgAndSurescriptsInfo?.homeCommunityId);
    const surescriptsOrgId = computed<string|undefined>(() => compData.orgAndSurescriptsInfo?.surescriptsOrgId);

    async function saveOrgInfo()
    {
        if (!orgInfo.value)
            return;            

        compData.savingOrgInfo = true;
        const result = await ChoiceApi.surescriptsRLE.saveRecordLocatorOrganizationInfo(orgInfo.value);
        compData.savingOrgInfo = false;
        if (!result.successful)
        {
            await popAlert(`Failed to save organization information for Surescripts Record Locator: ${result.msg ?? "Reason not known"}`, DisplayStyle.Danger);
            return;
        }
        showToast("RLE organization information saved successfully.", DisplayStyle.Success);   
    }

    function tstOutput()
    {
        console.log("Bad Number: " + orgInfo.value?.phone);
    }
</script>

<template>
    <PageTitle :securityScreenName="AppScreens.SurescriptsRecordLocatorInterfaceSetup">
        <template #title>
            Record Locator Organization Information
        </template>
        <template #subtitle>
            <MessageBadge>
                <MessageText :displayStyle="DisplayStyle.Warning">
                    Home Communitity ID:
                </MessageText>
                {{ homeCommunityId }}
            </MessageBadge>
            <MessageBadge :displayStyle="surescriptsOrgId ? undefined : DisplayStyle.Danger">
                <MessageText :displayStyle="DisplayStyle.Warning">
                    Surescripts Org ID:
                </MessageText>
                {{ surescriptsOrgId ?? "N/A" }}
            </MessageBadge>
        </template>
        <template #permitted>
            <AlertSection :type="DisplayStyle.Info">
                The following fields are used to ensure Surescripts Record Locator can connect and function properly.
            </AlertSection>

            <template v-if="orgInfo && compData.initialized">                
                <Row :bottomPadding="2">
                    Organization Name
                    <TextInput v-model:textValue="orgInfo.organizationName" 
                        :required="true" 
                        :disabled="compData.savingOrgInfo"
                        @validationState="(state, msg) => validateSection?.sinkValidation('Organization Name', state, msg)" />
                </Row>

                <Row :bottomPadding="1">
                    Phone Number
                    <PhoneInput v-model="orgInfo.phone"
                        :required="true"
                        :disabled="compData.savingOrgInfo"
                        @validationState="(state, msg) => validateSection?.sinkValidation('Phone Number', state, msg)"  />
                </Row>

                <Row :bottomPadding="4">
                    Fax Number
                    <PhoneInput v-model="orgInfo.fax"
                        :disabled="compData.savingOrgInfo"
                        @validationState="(state, msg) => validateSection?.sinkValidation('Fax Number', state, msg)"  />
                </Row>

                <Row :bottomPadding="1">
                    Street
                    <TextInput v-model:textValue="orgInfo.street" 
                        :required="true" 
                        :disabled="compData.savingOrgInfo"
                        @validationState="(state, msg) => validateSection?.sinkValidation('Street', state, msg)" />
                </Row>

                <Row :bottomPadding="1">
                    <div class="row">
                        <div class="col-sm-6">
                            City
                            <TextInput v-model:textValue="orgInfo.city" 
                                :required="true"
                                :disabled="compData.savingOrgInfo"
                                @validationState="(state, msg) => validateSection?.sinkValidation('City', state, msg)" />
                        </div>
                        <div class="col-sm-3">
                            State
                            <GeographicStateSelect v-model="orgInfo.stateAbbr" 
                                :required="true"
                                :disabled="compData.savingOrgInfo"
                                @validationState="(state, msg) => validateSection?.sinkValidation('State', state, msg)" />
                        </div>
                        <div class="col-sm-3">
                            Zip Code
                            <PostalCode v-model="orgInfo.zipCode" :allowCanadian="true" 
                                :required="true"
                                :disabled="compData.savingOrgInfo"
                                @validationState="(state, msg) => { validateSection?.sinkValidation('Zip Code', state, msg); }" />
                        </div>                
                    </div>
                </Row>

                <Validator ref="validateSection">
                    <ActionButtonV2 
                        :performAction="async () => await saveOrgInfo()" title="Save organization information used for RLE" 
                        :action="ActionButtonStyle.Save"
                        @performingAction="val => compData.savingOrgInfo = val">
                        Save
                    </ActionButtonV2>
                </Validator>                

            </template>       
        </template>
    </PageTitle>

    <template v-if="!orgInfo && compData.initialized">
        <AlertSection :type="DisplayStyle.Danger">
            Unable to fetch organization information.
        </AlertSection>
    </template>
</template>
